/**
 * A dropdown you can type into.
 *
 * The category lists grew past fifty names, and a native <select> makes a
 * person scroll for "Courier/Postage Exp" every time. This is the same choice
 * with a search box: focus it and type any part of the name (words in any
 * order), use the arrow keys and Enter, or click. Escape puts it back.
 *
 * It stays a plain input as far as the form is concerned: `required` works,
 * because the input is empty until something is chosen, and the browser's own
 * "please fill in this field" applies. The list is positioned against the
 * window rather than its parent, so a table that scrolls sideways does not
 * clip it.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

export interface Choice {
  value: string
  label: string
  //: A second, fainter line: a code, a note, what the choice means.
  hint?: string
}

//: Every word typed appears somewhere in the label or hint, in any order.
//: "post cour" finds "Courier/Postage Exp".
export function matchesQuery(choice: Choice, query: string): boolean {
  const words = query
    .toLowerCase()
    .split(/[\s/,.-]+/)
    .filter(Boolean)
  if (words.length === 0) return true
  const text = `${choice.label} ${choice.hint ?? ''}`.toLowerCase()
  return words.every((w) => text.includes(w))
}

interface Props {
  value: string
  onChange: (value: string) => void
  options: Choice[]
  //: The "nothing chosen" entry, offered first ("Uncategorised", "Any"...).
  //: Left out, nothing-chosen is not offered once something is.
  empty?: string
  //: Shown while nothing is chosen, when there is no `empty` label.
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  'aria-label'?: string
  id?: string
  title?: string
}

interface Spot {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

const LIST_MAX = 300

export default function SearchSelect({
  value,
  onChange,
  options,
  empty,
  placeholder,
  required,
  disabled,
  className,
  id,
  title,
  ...rest
}: Props) {
  const listId = useId()
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [spot, setSpot] = useState<Spot | null>(null)

  const chosen = options.find((o) => o.value === value)
  const all = useMemo(
    () => (empty !== undefined ? [{ value: '', label: empty }, ...options] : options),
    [empty, options],
  )
  const shown = useMemo(() => all.filter((o) => matchesQuery(o, query)), [all, query])

  //: Below the input when there is room, above it when there is not.
  const place = () => {
    const box = input.current?.getBoundingClientRect()
    if (!box) return
    const below = window.innerHeight - box.bottom - 8
    const above = box.top - 8
    const width = Math.max(box.width, 240)
    const left = Math.min(box.left, window.innerWidth - width - 8)
    if (below >= Math.min(LIST_MAX, 200) || below >= above) {
      setSpot({ left, width, top: box.bottom + 2, maxHeight: Math.min(LIST_MAX, below) })
    } else {
      setSpot({
        left,
        width,
        bottom: window.innerHeight - box.top + 2,
        maxHeight: Math.min(LIST_MAX, above),
      })
    }
  }

  useLayoutEffect(() => {
    if (open) place()
  }, [open])

  useEffect(() => {
    if (!open) return
    const follow = () => place()
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', follow)
    return () => {
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', follow)
    }
  }, [open])

  //: Keep the highlighted row in view as the arrow keys move it.
  useEffect(() => {
    if (!open) return
    const row = list.current?.children[active] as HTMLElement | undefined
    row?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const start = () => {
    if (disabled || open) return
    setQuery('')
    const at = all.findIndex((o) => o.value === value)
    setActive(at < 0 ? 0 : at)
    setOpen(true)
  }

  const finish = () => {
    setOpen(false)
    setQuery('')
  }

  const pick = (choice: Choice | undefined) => {
    finish()
    if (choice && choice.value !== value) onChange(choice.value)
  }

  return (
    <div className={`search-select${className ? ` ${className}` : ''}`}>
      <input
        ref={input}
        id={id}
        title={title ?? chosen?.label}
        role="combobox"
        aria-label={rest['aria-label']}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        required={required}
        disabled={disabled}
        value={open ? query : (chosen?.label ?? '')}
        placeholder={
          open ? (chosen?.label ?? 'Type to search') : (empty ?? placeholder ?? 'Choose…')
        }
        onFocus={start}
        onClick={start}
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
          if (!open) setOpen(true)
        }}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) start()
            else setActive((i) => Math.min(i + 1, shown.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            if (open) {
              // Choosing, not submitting the form around it.
              e.preventDefault()
              pick(shown[active])
            }
          } else if (e.key === 'Escape') {
            if (open) {
              e.preventDefault()
              e.stopPropagation()
              finish()
            }
          }
        }}
      />
      {open && spot && (
        <ul
          ref={list}
          id={listId}
          role="listbox"
          className="search-select-list"
          style={{
            left: spot.left,
            width: spot.width,
            top: spot.top,
            bottom: spot.bottom,
            maxHeight: spot.maxHeight,
          }}
        >
          {shown.length === 0 ? (
            <li className="search-select-none">Nothing matches “{query}”</li>
          ) : (
            shown.map((o, i) => (
              <li
                key={o.value || '∅'}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={o.value === value}
                className={`${i === active ? 'active' : ''}${o.value === value ? ' chosen' : ''}`}
                // Before the input's blur, which would close the list first.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(o)
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className={o.value === '' ? 'faint' : undefined}>{o.label}</span>
                {o.hint && <span className="search-select-hint">{o.hint}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
